// ╔══════════════════════════════════════════════════════════════╗
// ║ host i18n 字典 —— atomic key 池子,DB 通过 *Key/*Keys 字段引用    ║
// ║ 本文件由 scripts/write_locales_with_comments.mjs 生成           ║
// ║                                                                ║
// ║ 命名空间结构:                                                   ║
// ║   1. shared 网站 UI(exit/modal/.../common)                    ║
// ║   2. 跨游戏共用桶(paytable/rules/history/historyDetail)       ║
// ║   3. 游戏独占(bskg/fhcq/mjhl/mjhl2/sjcz/sjddj/sjnw/wldd/xltb/xltb2) ║
// ║                                                                ║
// ║ key 编号约定(在同一对象内):                                     ║
// ║   title*       段/页标题                                        ║
// ║   subtitle*    段副标题                                         ║
// ║   rule*        规则条目                                         ║
// ║   label*       字段标签(historyDetail 常用)                    ║
// ║   formula*     公式示例(paytable winRule 用)                  ║
// ║   symbolName*  符号名(百搭/夺宝)                              ║
// ║ 数字升序连续 1..N,删除某条后用 cleanup_and_renumber.mjs 重排    ║
// ║                                                                ║
// ║ 维护流程:                                                       ║
// ║   - 改文案 → 直接编辑此文件,push 后刷新 iframe 立刻生效         ║
// ║   - 加 key → 在合适命名空间下新增,然后 admin 后台填 *Key 引用   ║
// ║   - 删 key → DB 先去掉引用,再 cleanup_and_renumber.mjs 一键清理 ║
// ╚══════════════════════════════════════════════════════════════╝

export default {
  // ┌──────────────────────────────────────────────────────────────┐
  // │ shared 网站自身 UI 文案(host 弹窗、错误提示、加载态等)         │
  // └──────────────────────────────────────────────────────────────┘

  // 游戏退出确认弹窗
  exit: {
    title: 'Game exited safely',
    button: 'Return to Game',
    desc: 'Click the button below to return to the game.',
  },

  // 通用 modal 文案
  modal: {
    loading: 'Loading...',
  },

  // 网络重试 toast
  retry: {
    toast: 'Network is busy, retrying now... ({ordinal} time)',
  },

  // 竖屏提示
  orientation: {
    title: 'Please switch back to portrait mode',
    subtitle: 'to continue the game',
  },

  // 余额弹窗
  balance: {
    title: 'Balance',
    retry: 'Retry',
    walletLabel: 'Cash Wallet',
  },

  // 通用错误提示(common.errors.*)
  common: {
    errors: {
      fetchFailed: 'Failed to fetch balance, please retry.',
      missingLogin: 'Missing login information',
      networkError: 'Network error',
      tokenExpired: 'Login expired',
    },
  },

  // ┌──────────────────────────────────────────────────────────────┐
  // │ 跨游戏共用内容(≥2 个游戏引用)                                 │
  // │   编号规则:title* / subtitle* / rule* / label* / formula* / symbolName*  │
  // └──────────────────────────────────────────────────────────────┘

  // 赔付表段标题、规则文本、符号名、公式示例
  paytable: {
    title: 'Paytable',
    title1: 'Winning Bet Lines',
    title2: 'Maximum Win',
    title3: 'Free Spins Feature',
    title4: 'Multiplier Reel',
    title5: 'Symbol Payout Values',
    label1: 'From Above Example:',
    label2: 'From Above Example:',
    formula1: '2 x 3 x 2 x 1 = 12',
    formula2: '30 x 12 = 360',
    symbolName1: 'Scatter Symbol',
    symbolName2: 'Wild Symbol',
    rule1:
      'Bet ways win if the winning symbols are in succession from the leftmost reel to the right.',
    rule2:
      'Total number of winning bet ways for each symbol are calculated by multiplying the number of adjacent winning symbols on each symbols from the leftmost reel to the right.',
    rule3: 'The winning symbol payout is multiplied by the number of winning bet ways.',
    rule4:
      'After the payout of every round is made, all winning symbols will explode allowing the symbols above them to cascade down for a new round.',
    rule5:
      'Additional winning combination will be tallied in every round until no more winning combination can be tallied.',
    rule6: 'All wins shown in cash.',
    rule7: 'The maximum win amount is 5,000x of the bet amount.',
    rule8:
      'If the total win in the main game or the Free Spins Feature reaches 5,000x of the bet amount, the spin ends.',
    rule9: 'Free spins can be retriggered.',
    rule10:
      '3 Scatter symbols appearing anywhere will trigger the Free Spins Feature with 10 free spins. Each additional Scatter symbol will trigger 2 more free spins.',
    rule11:
      'After wins are paid, the total win multiplier will remain until it is combined with the total win multiplier af the next winning free spin.',
    rule12:
      '3 Scatter symbols appearing anywhere will trigger the Free Spins Feature with 12 free spins. Each additional Scatter symbol will trigger 2 more free spins.',
    rule13:
      'During the Free Spins Feature, the multipliers above the reels will be increased to x3, x6, x9 and x15 respectively.',
    rule14: 'At the start of every free spin, the win multiplier is x8.',
    rule15:
      'During any free spin, if there are one or more winning symbols on the reels, after wins are paid and the new symbols have cascaded down, the win multiplier will be doubled.',
    rule16:
      'During any spins, all wins will be multiplied by the multiplier shown above the reels, starting with x1 in the first round.',
    rule17: "Any win in the first round will increase the second round's multiplier to x2.",
    rule18: "Any win in the second round will increase the third round's multiplier to x3.",
    rule19:
      "Any win in the third round will increase the fourth round's (and beyond) multiplier to x5.",
    rule20: 'At the start of any main game spin, the win multiplier is x1.',
    rule21: 'Wild symbol substitutes for all symbols except Scatter symbol.',
    rule22:
      'For every winning combination, payout is made for each symbol according to the Paytable.',
    rule23: 'Wild symbol appears on reels 2, 3 and 4 only.',
    rule24:
      'During any spin, some symbols (excluding Wild symbol and Scatter symbol) in reels 3 and/or 4 may appear as gold framed symbols.',
    rule25:
      'At every new round after the new symbols have cascaded down, any gold framed symbol(s) that is involved in a win in the previous round will be transformed into Wild symbol(s).',
    title6: '1,024 Ways',
    title7: 'Gold Plated Symbols',
    title8: 'Gold Framed Symbols',
    title9: 'Winning symbols collection bar',
  },

  // 规则页段标题、规则文本、mainGame 操作说明、additionalInfo 附加信息(跨游戏共享)
  rules: {
    title: 'Game Rules',
    rule1: 'Simultaneous wins on different bet ways are added.',
    rule2:
      'Free Spins Feature is played at the same bet size and bet level as the spin that triggered the Free Spins Feature.',
    rule3: 'The Bet Size is set using the "Bet Size" selector.',
    rule4: 'The Bet Level is set using the "Bet Level" selector.',
    rule5: 'The Bet Amount is set using the "Bet Amount" selector.',
    rule6: '"Cash Wallet" displays the cash available for wager.',
    rule7: '"Auto Spin" automatically plays the game for a selected number of rounds.',
    rule8: 'Winning combinations and payouts are made according to the "Paytable".',
    rule9:
      'A bet way win in cash is equal to the value shown in the "Paytable"  multiplied by the bet size and bet level.',
    rule10:
      'Bet ways win if the winning symbols are in succession from the leftmost reel to the right.',
    rule11:
      'Total number of winning bet ways for each symbol are calculated by multiplying the number of adjacent winning symbols on each symbols from the leftmost reel to the right.',
    rule12:
      'The game is played with 20 base bet for 1,024 ways (fixed), bet level of 1 to 10 and bet size of 0.03 to 0.90.',
    rule13:
      'The game is played with 20 bet lines (fixed), bet level of 1 to 10 and bet size of 0.03 to 0.90.',
    rule14: 'Only the highest win per bet line is paid.',
    additionalInfo: {
      title: 'Additional Information',
      content: {
        '0': 'Malfunction voids all pays and plays.',
        '1': 'In the event of disputes over game results, all game results that are shown in the game history page are absolute and final.',
      },
    },
    bonusMultiplier: 'Multiplier',
    mainGame: {
      title: 'Main Game',
      items: {
        autoSpin: {
          title: 'Auto Spin',
          descriptions: {
            '0': 'Auto Spin automatically plays the game for a selected number of spins.',
            '1': 'Tap on the values to select number of Auto Spins.',
          },
        },
        bet: {
          title: 'Bet Amount',
          descriptions: {
            '0': 'Tap to display the Bet Options.',
            '1': 'Bet Size: Scroll to select the Bet Size.',
            '2': 'Bet Level: Scroll to select the Bet Level.',
            '3': 'Bet Amount: Scroll to select the Bet Amount.',
            '4': 'Max Bet: Tap to set Bet Size and Bet Level to maximum value.',
          },
        },
        close: {
          title: 'Close',
          descriptions: 'Tap to return to the Main Game.',
        },
        history: {
          title: 'History',
          descriptions: {
            '0': 'Shows details of the previous games played. Scroll down to the end to load more records.',
            '1': 'Tap({dateIcon})to select the dates of games to be shown in History.',
          },
        },
        minus: {
          title: 'Minus',
          descriptions: 'Tap to reduce the Bet Amount.',
        },
        paytable: {
          title: 'PayTable',
          descriptions: 'Shows winning combinations and paytable.',
        },
        plus: {
          title: 'Plus',
          descriptions: 'Tap to increase the Bet Amount.',
        },
        rules: {
          title: 'Rules',
          descriptions: 'Shows the game rules and button functions.',
        },
        settings: {
          title: 'More Settings',
          descriptions: 'Tap to access additional settings.',
        },
        sound: {
          title: 'Sound',
          descriptions: 'Tap to turn sound ON or OFF.',
        },
        spin: {
          title: 'Spin',
          descriptions: {
            '0': 'Tap to start spin at the current Base Bet, Bet Size and Bet Level.',
            '1': 'Tap the button or the game area during a spin to stop the reels.',
            '2': 'PC Version only',
            '3': 'Press the <Space> key to start spin at the current Base Bet, Bet Size and Bet Level.',
            '4': 'Press and Hold <Space> key will continue game spins until it is released.',
            '5': 'When the reels are spinning, press the <Space> key to skip the reels spinning animation and reveal the reel results (subject to internet connection stability).',
            '6': 'Whenever there is exactly one on-screen button available to press, press the <Space> key to activate the button.',
          },
        },
        stop: {
          title: 'Stop',
          descriptions:
            'Tap to stop Auto Spin. The number on the button indicates the remaining number of spins for Auto Spin.',
        },
        turbo: {
          title: 'Turbo Spin',
          descriptions:
            'Tap to enable or disable the Turbo Spin to reduce the duration of reel spins in the main game.',
        },
        wallet: {
          title: 'Wallet Balance',
          descriptions: 'Tap to display the balance of available wallets.',
        },
        win: {
          title: 'Win Amount',
          descriptions: 'Tap to display the Game History.',
        },
      },
    },
    rule15: 'Simultaneous wins on different bet lines are added.',
    rule16:
      'A combination win in cash is equal to the value shown in the “Paytable” multiplied by bet size and bet level.',
    rule17: 'Gold Plated symbols appears on reels 2, 3 and 4 only.',
  },

  // 历史页 UI 文案(列名、过滤标签、状态提示)
  history: {
    title: 'Game History',
    columns: {
      bet: 'Bet',
      orderId: 'Transaction',
      time: 'Time',
      win: 'Profit',
    },
    errors: {
      configLoadFailed: 'Configuration failed to load',
      fetchDataFailed: 'Failed to fetch data',
    },
    filterLabels: {
      confirm: 'Confirm',
      custom: 'Custom',
      endDate: 'End',
      last7days: 'Last 7 days',
      selectRange: 'Select Date Range',
      startDate: 'Start',
      today: 'Today',
    },
    statsLabels: {
      records: 'records',
    },
    statusLabels: {
      error: 'Load Failed',
      loading: 'Loading...',
      noData: 'No Game History',
      noMoreData: 'All record(s) displayed',
      retry: 'Tap to Retry',
    },
  },

  // 历史详情页字段标签(由 game_history_detail_config.config.labelKeys 引用)
  historyDetail: {
    label1: 'No Winning Combination',
    label2: 'Round',
    label3: 'Bet Size x Bet Level x Symbol Payout Values',
    label4: '3 of a Kind',
    label5: '4 of a Kind',
    label6: '5 of a Kind',
    label7: '6 of a Kind',
    label8: '7 of a Kind',
    label9: '8 of a Kind',
    label10: 'Bet Level',
    label11: 'Seven of a Kind',
    label12: 'Eight of a Kind',
    label13: 'Normal Spin',
    label14: 'Free Spin: {index}/{total}',
    label15: 'Bet Size x Bet Level x Symbol Payout Values x Way(s) x Multiplier',
    label16: '{count} of a Kind',
    label17: 'Round  {n}/{total}',
    balance: 'Balance',
    bet: 'Bet',
    betSize: 'Bet Size',
    bonusMultiplier: 'Win Multiplier',
    close: 'Close',
    freeSpins: 'Free Spins',
    goldenPrefix: 'Golden',
    multiplier: 'Multiplier',
    symbol: 'Symbol',
    totalWin: 'Total Win',
    transaction: 'Transaction',
    win: 'Profit',
    winInfo: 'Payout',
    winWays: 'way(s)',
  },

  // ┌──────────────────────────────────────────────────────────────┐
  // │ 游戏独占文案(每个游戏自己的 paytable / rules 子命名空间)        │
  // │   仅被该游戏(或同系列游戏:xltb/xltb2、mjhl/mjhl2)引用          │
  // └──────────────────────────────────────────────────────────────┘

  // 宝石矿工(Gemstones Gold)
  bskg: {
    // ── 赔付表专属文案 ──
    paytable: {
      rule1: 'The maximum win amount is 7,500x of the bet amount.',
      rule2:
        'If the total win in the main game or the Free Spins Feature reaches 7,500x of the bet amount, the spin ends.',
      rule3:
        'At the start of every free spin, 6 Multiplier symbols with values ranged from x2 to x500 will appear on random positions in the multiplier reel, each being covered by 2 blockers.',
      rule4:
        '2 Scatter symbols appearing anywhere during the Free Spins Feature will award 5 more free spins.',
      rule5:
        'At the start of every spin, 3 Multiplier symbols with values ranged from x2 to x500 will appear on random positions in the multiplier reel, each being covered by 3 blockers.',
      rule6:
        'At every round, if there are 1 or more winning symbols above any Multiplier symbols, a blocker is removed from those Multiplier symbols.',
      rule7:
        'When 3 blockers are removed from the Multiplier symbol, the Multiplier symbol will be fully revealed.',
      rule8:
        'At the end of the spin, all multipliers from any fully revealed Multiplier symbols will be added together and the total win of that spin will be multiplied by the total win multiplier.',
      rule9:
        'At every spin, any combinations of 8 or more matching symbols appearing anywhere on the reels will result in a winning combination.',
    },
    // ── 规则页专属文案 ──
    rules: {
      subtitle1: 'Gemstones Gold',
      rule1:
        'Gemstones Gold is a 5 by 6 symbols combination video slot featuring the multiplier reel with multipliers up to x500.',
      rule2:
        'The game is played with 20 base bet (fixed), bet level of 1 to 10 and bet size of 0.03 to 0.90.',
    },
    // ── 历史详情专属文案 ──
    historyDetail: {
      // 结算回合标签:bskg render 直接 t() 读,不走 DB labelKeys
      settlementMultiplier: 'Win Multiplier',
      settlementBonus: 'Multiplier Win',
      // 赢奖情况公式 label(bskg 仅 betSize x betLevel x payout;无 ways/multiplier)
      formulaLabel: 'Bet Size x Bet Level x Symbol Payout Values',
    },
  },

  // 凤凰传奇(Phoenix Rises)
  fhcq: {
    // ── 赔付表专属文案 ──
    paytable: {
      title1: '243 Ways',
      symbolName1: 'Free Spins Symbol',
      rule1:
        '5, 6 or 7 Free Spins symbols appearing anywhere will trigger the Free Spins Feature with 12 free spins.',
      rule2:
        'If the triggering game has 5 Free Spins symbols, the win multiplier in the free spins then starts at x1 and every free spin increases the multiplier by 1.',
      rule3:
        'If the triggering game has 6 Free Spins symbols, the win multiplier in the free spins then starts at x2 and every free spin increases the multiplier by 2.',
      rule4:
        'If the triggering game has 7 Free Spins symbols, the win multiplier in the free spins then starts at x3 and every free spin increases the multiplier by 3.',
      rule5: 'The maximum multiplier value achievable is x150.',
      rule6:
        '5 Free Spins symbols appearing anywhere during the Free Spins Feature will award 12 more free spins.',
      rule7: 'Wild symbol appears on reels 2, 3 and 4 only.',
    },
    // ── 规则页专属文案 ──
    rules: {
      subtitle1: 'Phoenix Rises',
      rule1:
        'Phoenix Rises is a 5-reel, 3-row video slot featuring Free Spins Feature with increasing multipliers up to x150.',
      rule2:
        'The game is played with 20 base bet for 243 ways (fixed), bet level of 1 to 10 and bet size of 0.03 to 0.90.',
    },
    // ── 历史详情专属文案 ──
    historyDetail: {
      // 普通旋转赢奖公式 label(无奖金倍数;免费旋转走 DB labelKeys.formulaLabel_freeSpin)
      formulaLabel: 'Bet Size x Bet Level x Symbol Payout Values x Way(s)',
    },
  },

  // 麻将胡了(Mahjong Ways)
  mjhl: {
    // ── 赔付表专属文案 ──
    paytable: {
      formula1: '1 x 3 x 2 = 6',
      formula2: '10 x 6 = 60',
      rule1:
        'During any spins, some symbols (excluding Wild symbol and Scatter symbol) in reels 2, 3 and/or 4 may appear in gold.',
      rule2:
        'At every new round after the new symbols have cascaded down, any gold plated symbol(s) that is involved in a win in the previous round will be transformed into Wild symbol(s).',
      rule3:
        'During the Free Spins Feature, the multipliers above the reels will be increased to x2, x4, x6 and x10 respectively.',
    },
    // ── 规则页专属文案 ──
    rules: {
      title: 'Mahjong Ways',
      rule1:
        'Mahjong Ways is a 5-reel, 4-row video slot featuring Wild symbol transformation and free spins with increasing multiplier.',
    },
  },

  // 麻将胡了2(Mahjong Ways 2,阿努比斯主题)
  mjhl2: {
    // ── 赔付表专属文案 ──
    paytable: {
      title1: '2,000 Ways',
      rule1:
        'During the Free Spins Feature, all symbols (excluding Wild symbol and Scatter symbol) in reel 3 will appear in gold.',
      rule2:
        'The multipliers above the reels will be increased to x2, x4, x6 and x10 respectively.',
    },
    // ── 规则页专属文案 ──
    rules: {
      subtitle1: 'Mahjong Ways 2',
      rule1:
        'Mahjong Ways 2 is a 5-reel, 4-row (with an additional row for each of reel 2, 3 and 4) video slot featuring Wild symbol transformation and free spins with increasing multiplier.',
      rule2:
        'The game is played with 20 base bet for 2,000 ways (fixed), bet level of 1 to 10 and bet size of 0.03 to 0.90.',
    },
  },

  // 赏金船长(Captain's Bounty)
  sjcz: {
    // ── 赔付表专属文案 ──
    paytable: {
      rule1:
        '3 Scatter symbols appearing anywhere will trigger the Free Spins Feature with 10 free spins. Each additional Scatter symbol will trigger 5 more free spins.',
    },
    // ── 规则页专属文案 ──
    rules: {
      subtitle1: "Captain's Bounty",
      rule1:
        "Captain's Bounty is a 5-reel, 3-row video slot featuring cascading symbols with increasing multipliers.",
    },
  },

  // 赏金大对决(Wild Bounty Showdown)
  sjddj: {
    // ── 赔付表专属文案 ──
    paytable: {
      title1: '3,600 Ways',
      formula1: '2 x 2 x 2 = 8',
      formula2: '10 x 8 = 80',
      rule1:
        'During any spin, if there are one or more winning symbols on the reels, after wins are paid and the new symbols have cascaded down, the win multiplier will be doubled.',
      rule2: 'The maximum win multiplier is x1,024.',
      rule3: 'Gold Framed symbols appears on reels 3 and 4 only.',
    },
    // ── 规则页专属文案 ──
    rules: {
      subtitle1: 'Wild Bounty Showdown',
      rule1:
        'Wild Bounty Showdown is a 6-reel (3 rows in reels 1 and 6, 4 rows in reels 2 and 5, 5 rows in reels 3 and 4) video slot featuring Gold Framed Symbols and increasing multiplier.',
      rule2:
        'The game is played with 20 base bet for 3,600 ways (fixed), bet level of 1 to 10 and bet size of 0.03 to 0.90.',
      rule3: 'Gold Framed symbols appears on reels 3 and 4 only.',
    },
  },

  // 赏金女王(Queen Of Bounty)
  sjnw: {
    // ── 赔付表专属文案 ──
    paytable: {
      rule1:
        'Players may choose to start the Free Spins Feature with: 20 free spins with multipliers of x1/x2/x3/x5, 10 free spins with multipliers of x3/x6/x9/x20, or 5 free spins with multipliers of x6/x12/x18/x40.',
      rule2:
        'During any spin, all wins will be multiplied by the multiplier shown above the reels, starting with x1 in the first round.',
      rule3: "Any win in the first round will increase the second round's multiplier to x2.",
      rule4: "Any win in the second round will increase the third round's multiplier to x3.",
      rule5:
        "Any win in the third round will increase the fourth round's (and beyond) multiplier to x5.",
    },
    // ── 规则页专属文案 ──
    rules: {
      subtitle1: 'Queen Of Bounty',
      rule1: '3 or more Scatter symbols appearing anywhere will trigger the Free Spins Feature.',
      rule2:
        'If the Free Spins Feature is triggered by more than 3 Scatter symbols, each additional Scatter symbol will add 4 more free spins to the 20 free spins selection; 2 more free spins to the 10 free spins selection; and 1 more free spin to the 5 free spins selection.',
      rule3:
        'Tap on any selection to begin the free spins with the stated number of free spins and its respective multipliers.',
      rule4:
        'Queen of Bounty is a 5-reel, 3-row video slot featuring cascading symbols with increasing multipliers and free spins selections.',
    },
  },

  // 亡灵大盗(Wild Bandito)
  wldd: {
    // ── 赔付表专属文案 ──
    paytable: {
      rule1:
        'During any free spin, if there are one or more winning symbols on the reels, after wins are paid and the new symbols have cascaded down, the win multiplier will be increased by 1 .',
      rule2:
        'During any spins, if there are one or more winning symbols on the reels, after wins are paid and the new symbols have cascaded down, the win multiplier will be increased by 1 .',
      rule3: 'Gold Framed symbols appears on reels 2, 3 and 4 only.',
      rule4:
        'During any spins, some symbols (excluding Wild symbol and Scatter symbol) in reels 2, 3 and/or 4 may appear in gold.',
    },
    // ── 规则页专属文案 ──
    rules: {
      subtitle1: 'Wild Bandito',
      rule1:
        'During the Free Spins Feature, all symbols (excluding Wild symbol and Scatter symbol) in reel 3 will appear in gold.',
      rule2: 'At the start of the Free Spins Feature, the win multiplier is x1.',
      rule3:
        'Wild Bandito is a 5-reel, 4-row video slot featuring Wild symbol transformation and free spins with increasing multiplier.',
    },
  },

  // 寻龙探宝(Dragon Hatch)
  xltb: {
    // ── 赔付表专属文案 ──
    paytable: {
      rule1:
        'At every spin, any combinations of 4 or more symbols touching horizontally or vertically will result in a winning combination.',
      rule2:
        'After the payout of every round is made, all winning symbols will explode, allowing the symbols above them to cascade down for a new round.',
      rule3: 'Wild symbol substitutes for all symbols.',
      rule4:
        'During any spin, if 50 or more winning symbols are collected, the Fire Dragon Feature is triggered during the round with no winning combination.',
      rule5:
        'During the Fire Dragon Feature, a randomly chosen symbol (excluding Wild symbol) will be added on the reels in a checkered pattern (it will not replace any Wild symbol).',
      rule6:
        'During any spin, if 10 or more winning symbols are collected, the Earth Dragon Feature is triggered during the round with no winning combination.',
      rule7:
        'During the Earth Dragon Feature, all low paying symbols will be removed from the reels.',
      rule8:
        'During any spin, if 70 winning symbols are collected, the Dragon Queen Feature is triggered during the round with no winning combination.',
      rule9:
        'During the Dragon Queen Feature, all low paying symbols will be transformed into high paying symbols or a Wild symbols.',
      rule10:
        'During any spin, if 30 or more winning symbols are collected, the Water Dragon Feature is triggered during the round with no winning combination.',
      rule11: 'During the Water Dragon Feature, 4 Wild symbols will be added to the reels.',
      rule12:
        'During any spin, winning symbols that explode will be accumulated in the collection bar above the reels.',
      rule13:
        'Once a certain amount of winning symbols have been collected, a feature will be triggered during the round with no winning combination.',
    },
    // ── 规则页专属文案 ──
    rules: {
      subtitle1: 'Dragon Hatch',
      rule1:
        'Dragon Hatch is a 5 by 5 arrangement of symbols video slot featuring various features that help in cascading more wins.',
      rule2:
        'The game is played with 10 base bet (fixed), bet level of 1 to 10 and bet size of 0.03 to 0.90.',
      rule3:
        'The positions of the additional Wild symbol are: 2nd row of the 2nd column, 4th row of the 2nd column, 2nd row of the 4th column and 4th row of the 4th column.',
      rule4: 'The collection bar will reset to zero at the start of any spin.',
      rule5: 'The maximum number of winning symbols that can be collected in a spin is 70.',
      rule6: 'All features triggered by the collection bar can only be triggered once in a spin.',
      rule7:
        'If there are 2 or more features waiting to be triggered, only one feature will be triggered during the round with no winning combination. The next feature can only be triggered during the next round with no winning combination. The features will be triggered in the following order: Earth Dragon Feature, Water Dragon Feature, Fire Dragon Feature, Dragon Queen Feature.',
      earthDragon: 'Earth Dragon Feature',
      fireDragon: 'Fire Dragon Feature',
      giantDragon: 'Dragon Queen Feature',
      waterDragon: 'Water Dragon Feature',
    },
  },

  // 寻龙探宝 2(Dragon Hatch 2)
  xltb2: {
    // ── 赔付表专属文案 ──
    paytable: {
      rule1: 'The maximum win amount is 10,000x of the bet amount.',
      rule2:
        'If the total win in the main game or the Free Spins Feature reaches 10,000x of the bet amount, the spin ends.',
      rule3: 'The maximum win amount is 2,500x of the bet amount.',
      rule4:
        'If the total win in the main game or the Bonus Feature reaches 2,500x of the bet amount, the spin ends.',
      rule5:
        'At every spin, any combinations of 4 or more symbols touching horizontally or vertically will result in a winning combination.',
      rule6:
        'After the payout of every round is made, all winning symbols will explode, allowing the symbols above them to cascade down for a new round.',
      rule7: 'Wild symbol substitutes for all symbols.',
      rule8: 'Winning combinations and payouts are made according to the "Paytable".',
      rule9:
        'During any spin, if 55 or more winning symbols are collected, the Fire Dragon Feature is triggered during the round with no winning combination.',
      rule10:
        'During the Fire Dragon Feature, a Wild symbol will be added to random positions on reel 1, 2, 4 and 5 respectively.',
      rule11:
        'During the Earth Dragon Feature, all low paying symbols will be removed from the reels.',
      rule12:
        'During any spin, if 10 or more winning symbols are collected, the Earth Dragon Feature is triggered during the round with no winning combination.',
      rule13:
        'During any spin, if 80 winning symbols are collected, the Dragon Queen Feature is triggered during the round with no winning combination.',
      rule14:
        'At the start of the Dragon Queen Feature, a Wild symbol will be added to the four corners of the reels respectively and will remain there until the end of the spin.',
      rule15:
        'During the Dragon Queen Feature, all low paying symbols will be transformed into high paying symbols.',
      rule16:
        'During any spin, if 30 or more winning symbols are collected, the Water Dragon Feature is triggered during the round with no winning combination.',
      rule17:
        'During the Water Dragon Feature, a randomly chosen symbol (either Earth Dragon symbol, Water Dragon symbol, Fire Dragon symbol or Dragon Eye symbol) will be added vertically to the second column of the reels and another randomly chosen symbol (either Earth Dragon symbol, Water Dragon symbol, Fire Dragon symbol or Dragon Eye symbol) will be added vertically to the fourth column of the reels.',
      rule18:
        'During any spin, winning symbols that explode will be accumulated in the collection bar above the reels.',
      rule19:
        'Once a certain amount of winning symbols have been collected, a feature will be triggered during the round with no winning combination.',
      rule20:
        'A Wild symbol is positioned in the third column and third row of the reels and will remain there for every round.',
      title1: 'Sticky Wild Symbol',
    },
    // ── 规则页专属文案 ──
    rules: {
      subtitle1: 'Dragon Hatch 2',
      rule1:
        'Dragon Hatch 2 is a 5 by 5 arrangement of symbols video slot featuring various features that help in cascading more wins.',
      rule2:
        'The game is played with 10 base bet (fixed), bet level of 1 to 10 and bet size of 0.03 to 0.90.',
      rule3: 'The collection bar will reset to zero at the start of any spin.',
      rule4: 'All features triggered by the collection bar can only be triggered once in a spin.',
      rule5: 'The maximum number of winning symbols that can be collected in a spin is 80.',
      rule6:
        'If there are 2 or more features waiting to be triggered, only one feature will be triggered during the round with no winning combination. The next feature can only be triggered during the next round with no winning combination. The features will be triggered in the following order: Earth Dragon Feature, Water Dragon Feature, Fire Dragon Feature, Dragon Queen Feature.',
      earthDragon: 'Earth Dragon Feature',
      fireDragon: 'Fire Dragon Feature',
      giantDragon: 'Dragon Queen Feature',
      waterDragon: 'Water Dragon Feature',
    },
  },
}
